import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authGet, authPost, authPut, authDelete } from '../../utils/auth';
import useRequireAdmin from '../../hooks/useRequireAdmin';
import {
  ArrowLeft, Plus, Trash2, Edit, GripVertical, ExternalLink,
  X, Map, Globe, AlertTriangle
} from 'lucide-react';
import regionsData from '../../data/regions.json';
import countriesData from '../../data/countries.json';
import { safeHref } from '../../utils/url';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const DESCRIPTION_MAX = 45;

// Region options from shared regions.json (same source the map filter uses)
const REGION_OPTIONS = Object.entries(regionsData).map(([slug, meta]) => ({
  value: slug,
  label: meta.name || slug,
  countries: meta.countries || [],
}));

// Build a display name lookup for country ISO codes
const COUNTRY_NAME = {
  AU: 'Australia', NZ: 'New Zealand', US: 'United States', CA: 'Canada',
  GB: 'United Kingdom', IE: 'Ireland', CH: 'Switzerland', AT: 'Austria',
  DE: 'Germany', FR: 'France', ES: 'Spain', IT: 'Italy', NL: 'Netherlands',
  BE: 'Belgium', SE: 'Sweden', NO: 'Norway', DK: 'Denmark', FI: 'Finland',
  PT: 'Portugal', GR: 'Greece', PL: 'Poland', CZ: 'Czechia', HU: 'Hungary',
  SG: 'Singapore', JP: 'Japan', HK: 'Hong Kong', KR: 'South Korea',
  BR: 'Brazil', MX: 'Mexico', AR: 'Argentina', CL: 'Chile', CO: 'Colombia',
  ZA: 'South Africa', NG: 'Nigeria', KE: 'Kenya',
};

const countryLabel = (iso) => COUNTRY_NAME[iso] ? `${COUNTRY_NAME[iso]} (${iso})` : iso;

const COUNTRIES_FOR_REGION = (regionSlug) => {
  if (!regionSlug) return [];
  const meta = regionsData[regionSlug];
  if (!meta) return [];
  return [...meta.countries].sort((a, b) => countryLabel(a).localeCompare(countryLabel(b)));
};

// Sortable Exchange Item
const SortableExchangeItem = ({ exchange, onEdit, onDelete, deleting }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: exchange.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-card border border-border rounded-lg p-4 flex items-center gap-4 hover:border-primary transition-colors"
    >
      <div {...attributes} {...listeners} className="cursor-grab hover:text-primary">
        <GripVertical className="w-5 h-5 text-muted-foreground" />
      </div>

      <div className="flex-shrink-0 w-16 h-16 bg-muted rounded-lg flex items-center justify-center overflow-hidden">
        {exchange.logo_path ? (
          <img
            src={`${exchange.logo_path.replace('/uploads/', '/api/uploads/')}`}
            alt={exchange.name}
            className="w-full h-full object-contain"
          />
        ) : (
          <span className="text-2xl">💱</span>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-foreground truncate">{exchange.name}</h3>
        {exchange.description && (
          <p className="text-sm text-muted-foreground line-clamp-1">{exchange.description}</p>
        )}
        <div className="flex flex-wrap gap-1 mt-1">
          {exchange.show_on_map && (
            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex items-center gap-1">
              <Map className="w-3 h-3" /> On map
            </span>
          )}
          {exchange.region && (
            <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
              {exchange.region}
            </span>
          )}
          {exchange.country && (
            <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
              {exchange.country}
            </span>
          )}
          {exchange.is_p2p_global && (
            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">P2P global</span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        {exchange.url && (
          safeHref(exchange.url) ? (
            <a
              href={safeHref(exchange.url)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:text-primary-hover"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          ) : (
            <span className="text-muted-foreground" title={exchange.url}>
              <ExternalLink className="w-4 h-4" />
            </span>
          )
        )}
        <Button variant="ghost" size="sm" onClick={() => onEdit(exchange)}>
          <Edit className="w-4 h-4" />
        </Button>
        <Button variant="ghost" size="sm" disabled={deleting} onClick={() => onDelete(exchange.id)}>
          <Trash2 className="w-4 h-4 text-red-500" />
        </Button>
      </div>
    </div>
  );
};

const ExchangeManagement = () => {
  const navigate = useNavigate();
  // Server-verified admin gate.
  const { user, checking } = useRequireAdmin();
  const [exchanges, setExchanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editingExchange, setEditingExchange] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [form, setForm] = useState({
    name: '',
    description: '',
    url: '',
    logo_url: '',
    show_on_map: true,
    region: '',
    country: '',
    is_p2p_global: false,
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchExchanges = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authGet('/admin/exchanges');
      setExchanges(res.data);
    } catch (error) {
      console.error('Error fetching exchanges:', error);
      toast.error('Failed to load exchanges');
    }
    setLoading(false);
  }, []);

  // Only load once the admin session is server-verified.
  useEffect(() => {
    if (user) fetchExchanges();
  }, [user, fetchExchanges]);

  const handleOpenDialog = (exchange = null) => {
    if (exchange) {
      setEditingExchange(exchange);
      // Back-compat: derive region/country from legacy `regions` array if split fields missing
      let region = exchange.region || '';
      let country = exchange.country || '';
      if ((!region || !country) && Array.isArray(exchange.regions)) {
        for (const r of exchange.regions) {
          if (typeof r !== 'string') continue;
          if (r.length === 2 && r === r.toUpperCase()) {
            if (!country) country = r;
            if (!region) {
              const slug = Object.entries(regionsData).find(([, m]) => (m.countries || []).includes(r));
              if (slug) region = slug[0];
            }
          } else if (regionsData[r.toLowerCase()] && !region) {
            region = r.toLowerCase();
          }
        }
      }
      setForm({
        name: exchange.name || '',
        description: (exchange.description || '').slice(0, DESCRIPTION_MAX),
        url: exchange.url || '',
        logo_url: '',
        show_on_map: exchange.show_on_map !== false,
        region,
        country,
        is_p2p_global: !!exchange.is_p2p_global,
      });
    } else {
      setEditingExchange(null);
      setForm({
        name: '',
        description: '',
        url: '',
        logo_url: '',
        show_on_map: true,
        region: '',
        country: '',
        is_p2p_global: false,
      });
    }
    setDialog(true);
  };

  // Region drives the country dropdown. Clear country if it no longer belongs.
  const handleRegionChange = (value) => {
    setForm(prev => {
      const countries = COUNTRIES_FOR_REGION(value);
      const nextCountry = countries.includes(prev.country) ? prev.country : '';
      return { ...prev, region: value, country: nextCountry };
    });
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Exchange name is required');
      return;
    }

    setUploading(true);
    try {
      const region = form.region || '';
      const country = form.country || '';
      const regions = [];
      if (region) regions.push(region);
      if (country) regions.push(country);

      const payload = {
        name: form.name.trim(),
        description: (form.description || '').trim().slice(0, DESCRIPTION_MAX) || null,
        url: form.url.trim() || null,
        logo_url: form.logo_url.trim() || null,
        order: editingExchange ? editingExchange.order : exchanges.length,
        show_on_map: form.show_on_map,
        region: region || null,
        country: country || null,
        regions,
        is_p2p_global: !!form.is_p2p_global,
        tags: [],
      };

      if (editingExchange) {
        await authPut(`/admin/exchanges/${editingExchange.id}`, payload);
        toast.success('Exchange updated');
      } else {
        await authPost('/admin/exchanges', payload);
        toast.success('Exchange created');
      }

      setDialog(false);
      fetchExchanges();
    } catch (error) {
      toast.error('Failed to save exchange');
    }
    setUploading(false);
  };

  // Open the confirmation dialog (replaces native window.confirm).
  const handleDelete = (id) => setDeleteTarget(id);

  const confirmDelete = async () => {
    if (deleteTarget == null) return;
    setDeleting(true);
    try {
      await authDelete(`/admin/exchanges/${deleteTarget}`);
      toast.success('Exchange deleted');
      fetchExchanges();
    } catch (error) {
      toast.error('Failed to delete exchange');
    }
    setDeleting(false);
    setDeleteTarget(null);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    // `over` is null when dropped outside any sortable target.
    if (over && active.id !== over.id) {
      const oldIndex = exchanges.findIndex(e => e.id === active.id);
      const newIndex = exchanges.findIndex(e => e.id === over.id);

      const newExchanges = arrayMove(exchanges, oldIndex, newIndex);
      setExchanges(newExchanges);

      // Save new order to backend
      try {
        await authPost('/admin/exchanges/reorder', {
          exchanges: newExchanges.map((e, i) => ({ id: e.id, order: i }))
        });
        toast.success('Order updated');
      } catch (error) {
        toast.error('Failed to save order');
        fetchExchanges();
      }
    }
  };

  if (checking || loading) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted">
      {/* Header */}
      <div className="bg-card border-b border-border">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <h1 className="text-2xl font-heading font-bold text-foreground">
                Exchange Management
              </h1>
            </div>
            <Button onClick={() => handleOpenDialog()}>
              <Plus className="w-4 h-4 mr-2" />
              Add Exchange
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="mb-4">
            <p className="text-sm text-muted-foreground">
              Manage Bitcoin-only exchanges displayed on the &quot;How To Accept&quot; page and map pages. Drag to reorder.
            </p>
          </div>

          {exchanges.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              No exchanges yet. Add your first exchange.
            </p>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={exchanges.map(e => e.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-3">
                  {exchanges.map(exchange => (
                    <SortableExchangeItem
                      key={exchange.id}
                      exchange={exchange}
                      onEdit={handleOpenDialog}
                      onDelete={handleDelete}
                      deleting={deleting}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      </div>

      {/* Exchange Dialog */}
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingExchange ? 'Edit Exchange' : 'Add Exchange'}</DialogTitle>
            <DialogDescription>
              {editingExchange ? 'Update the exchange details below.' : 'Add a new cryptocurrency exchange to the directory.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label>Exchange Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({...form, name: e.target.value})}
                placeholder="Bitaroo"
              />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label>Description</Label>
                <span className={`text-xs ${form.description.length > DESCRIPTION_MAX ? 'text-red-500' : 'text-muted-foreground'}`}>
                  {form.description.length}/{DESCRIPTION_MAX}
                </span>
              </div>
              <textarea
                className="w-full border border-border rounded-md px-3 py-2 min-h-[60px]"
                maxLength={DESCRIPTION_MAX}
                value={form.description}
                onChange={(e) => setForm({...form, description: e.target.value.slice(0, DESCRIPTION_MAX)})}
                placeholder="Short one-liner (max 45 chars)"
              />
            </div>

            <div>
              <Label>Website URL</Label>
              <Input
                value={form.url}
                onChange={(e) => setForm({...form, url: e.target.value})}
                placeholder="https://bitaroo.com.au"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Region</Label>
                <select
                  className="w-full border border-border rounded-md px-3 py-2 bg-background"
                  value={form.region}
                  onChange={(e) => handleRegionChange(e.target.value)}
                >
                  <option value="">— Select region —</option>
                  {REGION_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Country</Label>
                <select
                  className="w-full border border-border rounded-md px-3 py-2 bg-background disabled:opacity-50"
                  value={form.country}
                  disabled={!form.region}
                  onChange={(e) => setForm({...form, country: e.target.value})}
                >
                  <option value="">{form.region ? '— Select country —' : '(choose region first)'}</option>
                  {COUNTRIES_FOR_REGION(form.region).map(iso => (
                    <option key={iso} value={iso}>{countryLabel(iso)}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex items-center gap-3 border border-border rounded-lg p-3 bg-muted/30">
              <input
                type="checkbox"
                id="is_p2p_global"
                checked={form.is_p2p_global}
                onChange={(e) => setForm({...form, is_p2p_global: e.target.checked})}
                className="w-4 h-4 accent-primary"
              />
              <label htmlFor="is_p2p_global" className="text-sm cursor-pointer flex items-center gap-2">
                <Globe className="w-4 h-4" />
                Global P2P (fallback in regions with &lt;3 native exchanges)
              </label>
            </div>

            <div>
              <Label>Logo URL {editingExchange?.logo_path && "(enter new URL to replace)"}</Label>
              <Input
                value={form.logo_url}
                onChange={(e) => setForm({...form, logo_url: e.target.value})}
                placeholder="https://example.com/logo.png"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Enter a URL to download the logo. URL will be cleared after upload.
              </p>
              {editingExchange?.logo_path && (
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Current logo:</span>
                  <img
                    src={`${editingExchange.logo_path.replace('/uploads/', '/api/uploads/')}`}
                    alt="Current logo"
                    className="w-8 h-8 object-contain"
                  />
                </div>
              )}
            </div>

            {/* Map settings */}
            <div className="border border-border rounded-lg p-3 flex items-center gap-3">
              <input
                type="checkbox"
                id="show_on_map"
                checked={form.show_on_map}
                onChange={(e) => setForm({...form, show_on_map: e.target.checked})}
                className="w-4 h-4 accent-primary"
              />
              <label htmlFor="show_on_map" className="text-sm text-foreground cursor-pointer flex items-center gap-2">
                <Map className="w-4 h-4" /> Show on map pages (uses region + country above)
              </label>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={uploading}>
                {uploading ? 'Saving...' : (editingExchange ? 'Update' : 'Create')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteTarget != null} onOpenChange={(open) => { if (!open && !deleting) setDeleteTarget(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" strokeWidth={1.5} />
              Delete Exchange
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" disabled={deleting} onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button onClick={confirmDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700 text-white">
              <Trash2 className="w-4 h-4 mr-1" />
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExchangeManagement;
